import { AfterViewInit, Component, ComponentRef, ElementRef, HostBinding, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { FilterObj } from '../../obj/filter/filter.obj';
import { WizComponentManager } from '../../services/core/component/wiz-component.manager';
import { WizButton } from '../controls/button/wiz-button/wiz-button';
import { WizDateField } from '../controls/date/wiz-datefield/wiz-datefield';
import { WizTextInput } from '../controls/input/wiz-text-input/wiz-text-input';
import { WizCheckbox } from '../controls/button/wiz-checkbox/wiz-checkbox';
import { WizDropdown } from '../controls/wiz-dropdown/wiz-dropdown';
import { WizDatasource } from '../../services/collections/wiz-datasource';
import { FILTER_TYPE, SortField, WIZ_EVENT_TYPE, WizCollectionUtils, WizStringUtils } from '@wizeoo/akm-core-lib';
import { WizEventManager } from '../../events/wiz-event.manager';
import { WizEvent } from '../../events/wiz-event';

@Component({
  selector: 'filter-component',
  templateUrl: './filter.component.html'
})
export class FilterComponent implements AfterViewInit {
  @HostBinding('style.max-height') get doMaxHeight() { // bridage de la taille
    return this.maxHeight;
  }

  /** Text input de filtre de la liste de pays */
  @ViewChild('tiSearchFilter', { read: WizTextInput, static: true }) tiSearchFilter: WizTextInput;
  /** Link d'ouverture des filtres */
  @ViewChild('btnShowMoreFilter', { read: WizButton }) btnShowMoreFilter: WizButton;
  /** Link de reset des filtres */
  @ViewChild('btnResetFilter', { read: WizButton }) btnResetFilter: WizButton;

  // container HTML pour chargement des filtres
  @ViewChild('filterContainer', { read: ViewContainerRef, static: true }) filterContainer: ViewContainerRef;



  /** rendu */
  @Input() public maxHeight: string = '250px'; // taille maximale du panneau (ne pas écraser la liste)
  @Input() public advancedMode: boolean = false; // mode avancé, le détail est disponible

  public activeFilter: boolean = false; // un filtre actif
  public showMoreLabel: string = '$.common.more.filters.$'; // pour switch more / less

  /** configuration */
  public _isOpen: boolean = false; // le détail est affiché
  public set isOpen(value: boolean) {
    this._isOpen = value;
    if (this._isOpen) {
      this.showMoreLabel = '$.common.less.filters.$';
    } else {
      this.showMoreLabel = '$.common.more.filters.$';
    }
  }
  public setOpen(value: boolean): FilterComponent { // accesseur externe
    this.isOpen = value ? value: false;
    return this;
  }
  public getOpen(): boolean {
    return this._isOpen;
  }

  /** fonctionnel */
  public basicFields: string[]; // la collection des champs filtrés par le search générique
  private _filters: Map<string, FilterObj>; // la map de champs avancés. Si vide, advancedMode = false
  private initfilters: Map<string, any>;// clonage de la valeur initiale

  public getFilter(): FilterObj[] { // accesseur pour mémorisation dans le contextEngine
    if(this._filters) {
      return [...this._filters.values()];
    }
    return null;
  }

  public getFilterKeysMap(): Map<string, FilterObj>  { // accesseur direct sur la map pour construction du contexte
    return this._filters;
  }

  public getFilterKeys(): FilterObj[] { // accesseur pour mémorisation dans le contextEngine
    let res: FilterObj[] = [];
    if(this._filters) {
      this._filters.forEach((fo:FilterObj)=> {
        if(fo.v) {
          let resItem: FilterObj = new FilterObj();
          resItem.v = fo.getMinifyValue();
          resItem.f = fo.f;
          resItem.k = fo.k;
          res.push(resItem);
        }
      });
      return res;
    }

    return null;
  }


  public basicValue: string = null; // la valeur du search générique
  public getBasic(): string { // accesseur pour mémorisation dans le contextEngine
    return this.basicValue;
  }

  constructor(public elementRef: ElementRef) {
  }

  /** mise en place du listener de changement */
  ngAfterViewInit(): void {
    WizEventManager.addListener(this, this.elementRef, WIZ_EVENT_TYPE.CHANGE, this.onChange);
  }

  /**
   * méthode à inclure dans une filterFunc.
   * @param item : l'instance d'objet à filter / ou pas
   * @return true si l'objet n'est PAS filtré.
   */
  public checkVisible(item: any): boolean {
    if(item.deprecated === "1") {
      return false;
    }
    
    let partResult: boolean = false;
    if (this.basicValue) { // test générique, l'un des fields doit contenir la valeur en UpperCase
      this.basicFields.forEach((field: string) => { // pour tous les champs cibles
        if (!!item[field] && WizStringUtils.containUpper(item[field], this.basicValue)) {
          partResult = true;
        }
      });
    } else { // pas de critère générique
      partResult = true;
    }
    if (!partResult) { // texte générique et aucun match, sortie
      return false;
    }

    partResult = true; // rebelote sur les autres filtres
    // pour chaque paramètre de filtre
    if (this._filters){
      this._filters.forEach((filter: FilterObj, key: string) => {
        const itemValue: any = this.resolveItemValue(item, filter);
        // selon le type, tests différents
        switch (filter.k) {
          case FILTER_TYPE.BOOLEAN : // ce sont des options "en plus". On masque si pas de valeur de filtre et true sur l'objet
            if ((!filter.v || filter.v === false) && itemValue) {
              partResult = false; // fct anonyme, return sort simplement de la fonction
            }
            break;
          case FILTER_TYPE.TRUELEAN : // ce sont des options d'écrétage. Par défaut, on prend tout le monde. Si coché, on prend les "true"
            if (filter.v === true && (itemValue === null || !itemValue)) {
              partResult = false; // fct anonyme, return sort simplement de la fonction
            }
            break;
          case FILTER_TYPE.TEXT :
            if (!filter.v && itemValue) {
              partResult = false;
            }
            break;
          case FILTER_TYPE.LIST_MULTI :
            if (filter.v && [...filter.v].length > 0 ) { // c'est une collection. Il en faut au moins 1
              let multiResult: boolean = false;
              [...filter.v].forEach(element => {
                if ( item[filter.f] && [...item[filter.f]].indexOf(element) > -1 ) {
                  multiResult = true;
                }
              });
              if (!multiResult) { partResult = false; }
            }
            break;

          default:
            // TODO to be continued...
          break;
        }
      });
    }

    return partResult;
  }

  /**
   * Résolution de la valeur dans l'item pour le filtre à traité en paramètre
   * @param item L'item / VO dont il faut récupérer la value
   * @param filter L'objet FilterObj qui comporte les valeurs actuelles de filtres et le paramétrage du filtre
   * @returns 
   */
  private resolveItemValue(item: any, filter: FilterObj): any {
    // On donne la priorité au field renseigné dans le filterobj
    if (!!filter.f) {
      return item[filter.f];
    } else if (!!filter.filterFunction) {
      // Si une fonction est renseignée, on l'appelle en lui passant l'item et la valeur du filtre
      return filter.filterFunction(item, filter.v);
    }

    return null;
  }

  /**
   * alimentation des champs
   * @param basicFields: string[]; // la collection des champs filtrés par le search générique
   * @param basicValue : la valeur par défaut dans le search générique
   * @param filters: FilterObj[]; // la collection de champs avancés. Si vide, advancedMode = false
   * note: les filters peuvent déjà porter la valeur.
   */
  public configure( basicFields: string[], basicValue: string= null, filters: FilterObj[] = null): FilterComponent {
    this.basicFields = basicFields;
    this.basicValue = basicValue;
    if (filters){
      this._filters = new Map(filters.map(item => [item.f, item]));
      this.initfilters = new Map(filters.map(item => [item.f,item.v])); // valeurs d'init
    }
    return this;
  }

  /**
   * activation des filtres avec des valeurs externes (depuis le contexte utilisateur)
   * @param basicValue: le texte du search générique
   * @param filters: FilterObj[]; // la collection de champs avancés. Si vide, advancedMode = false
   * @param open: boolean
   */
  public applyContext(basicValue: string= null, values: FilterObj[] = null, open ?: boolean): FilterComponent {

    this.mergeFilters(values, true); // TODO pas de fireEvent depuis contexte....
    this.basicValue = basicValue; // attention à l'ordre, le merge fait un reset
    if (open) {
      this.setOpen(open);
    }
    return this;
  }

  /**
   * construction du rendu
   */
  public build(silent: boolean = false): FilterComponent {

    this.filterContainer.clear(); // vide le container

    this.activeFilter = false; // pas de filtre activé par défaut

    this.tiSearchFilter.wizValue = this.basicValue;
    if (this.basicValue) { // filtre global présent
      this.activeFilter = true;
    }


    if (this._filters) { // si au moins un champ de filtre, mode avancé
      // présentation des filtres dans le bon ordre
      const workingArray = Array.from(this._filters, ([key, value]) => (value)); // new Array(this.filters).map(pairs => pairs[0]);
      // advanced mode seulement si liste de filtres non vide
      if (workingArray && workingArray.length > 0) {
        this.advancedMode = true;
        const sortFields: SortField[] = [];
        sortFields.push({property: 'order', way: 'asc', numeric: true});
        sortFields.push({property: 'label', way: 'asc', numeric: false});
        WizCollectionUtils.doSort(workingArray, sortFields);

        // création des composants
        workingArray.forEach((filter: FilterObj) => {
          const comp: ComponentRef<any> =
          WizComponentManager.instance.addChildTo(this.filterFactory(filter),
                                                  this.filterContainer,
                                                  this.filterComponentArgs(filter));
          filter.compoInstance = comp.instance; // mémorisation du compo sur le filter
        });
      } else {
        this.advancedMode = false;
      }
    } else {
      this.advancedMode = false;
    }

    if (!silent) {
      this.fireEvent(); // signaler OK
    }
    return this;
  }

  /**
   * Pour un type de filtre donné, retourne la classe de rendu
   * @param le filtre
   * @return : la classFactory
   */
  private filterFactory(filter: FilterObj): any {
    switch (filter.k) {
      case FILTER_TYPE.BOOLEAN:
      case FILTER_TYPE.TRUELEAN:
        return WizCheckbox;
      case FILTER_TYPE.TEXT:
        return WizTextInput;
      case FILTER_TYPE.DATE:
        return WizDateField;
      case FILTER_TYPE.LIST_MULTI:
        return WizDropdown;
    }
  }

  /**
   * Pour un type de filtre donné, va construire les ...args de création et les valeurs
   * instanceProperties: {data: ...}, styleProperties: {...}
   * @param filter le filtre
   * @return : les arguments de construction
   */
  private filterComponentArgs(filter: FilterObj): any {
    const result: any = {instanceProperties: {}, styleProperties: {width: '100%'}};
    if (filter.filterData) {
      result.instanceProperties = filter.filterData;
    }
    result.instanceProperties.wizShowLabel = false; // on affiche pas les labels
    result.instanceProperties.wizPlaceholder = filter.label; // mais les prompts
    result.instanceProperties.manageModel = false;

    // gestion de la valeur initiale
    result.instanceProperties.wizValue = filter.v;
    if (filter.v) { // si à minima une valeur non nulle, filtre actif
      this.activeFilter = true;
    }

    switch (filter.k) {
      case FILTER_TYPE.BOOLEAN:
      case FILTER_TYPE.TRUELEAN:
        result.instanceProperties.wizShowLabel = true; // on affiche les labels pour les checks
        result.instanceProperties.wizLabel = filter.label;
        result.instanceProperties.wizChecked = filter.v; // pas de null sur la combo
        break;
      case FILTER_TYPE.DATE:
        break;
      case FILTER_TYPE.TEXT:
        result.instanceProperties.maxChar = 20; // limiter les saisies
        break;
      case FILTER_TYPE.LIST_MONO:
      case FILTER_TYPE.LIST_MULTI:
        // alimentation de la selectedValue par la datasource
        if (filter.v) {
          filter.filterData.wizDataProvider.select(filter.v);
        }
        break;
    }
    return result;
  }

  /**
   * remise à 0 de tous les filtres
   */
  public resetFilter(silent: boolean = false): void {
    this.basicValue = null;
    if (this._filters){
      this._filters.forEach((value: FilterObj, key: string) => {
        value.v = this.initfilters.get(key); // retour aux valeurs d'init
      });
    }

    this.build(silent); // on rebuild tout
    if (!silent) {
      this.fireEvent(); // on signale
    }
    
  }

  /**
   * sur alimentation du select, merge des "values" avec les filters
   * @param values les objets porteurs des valeurs
   * @param silent, pas de maj du contexte (on en vient)
   */
  private mergeFilters(values: FilterObj[], silent:boolean = false): void {
    this.resetFilter(silent);
    if (this._filters && values){
      values.forEach((val: FilterObj) => {
        const exist: FilterObj = this._filters.get(val.f);
        if (exist) {
          // mode liste, conversion des strings en VO
          if(exist.k === FILTER_TYPE.LIST_MULTI || exist.k === FILTER_TYPE.LIST_MONO) {
            const arr = [];
            const ds: WizDatasource<any> = exist.filterData.wizDataProvider;
            val.v.forEach( (key: string) => {
              const item: any = ds.getItemByKey(key);
              if (item) {
                arr.push(item);
              }
            });
            exist.v = arr;
          } else {
            exist.v = val.v;
          }
        }
      });
    }
  }

  /** afficher / masquer le détail */
  public toggleShowMore(): void {
    this.isOpen = !this._isOpen;
  }

  /**
   * changement de l'un des filtres, affectation de la valeur
   * @param WizEvent fire par les compos
   */
  private onChange(e: WizEvent): void {
    const compo = e.component;
    const value = e.value;
    // console.log('onChange', compo, value);

    if (compo === this.tiSearchFilter) {
      this.basicValue = value;
    }

    if (this._filters) {
      this._filters.forEach((item: FilterObj, key: string) => {
        if (item.compoInstance === compo) {
          item.setValues(value);
        }
      });
    }


    this.stillFilter(); // pour synchroniser l'IHM (bt Reset)
    this.fireEvent(); // et signaler
  }

  /** contrôle s'il existe encore un filtre, pour synchroniser l'IHM */
  private stillFilter(): void {
    let hasFilter: boolean = false;
    if (this.basicValue) {
      hasFilter = true;
    }
    if (this._filters) {
      this._filters.forEach((item: FilterObj, key: string) => {
        const initV: any = this.initfilters.get(key);
        if (item.v !== initV) {
          hasFilter = true;
        }
      });
    }
    this.activeFilter = hasFilter;
  }

  /**
   * dispatch de l'event pour signaler le refresh nécessaire
   */
  private fireEvent(): void {
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.FILTER, null, this));
  }

}
